package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.ImageCreditStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

// --- Constants ---
private val HERO_IMAGE_MAX_WIDTH = 600.px
private val FIGURE_IMAGE_MAX_WIDTH = 600.px

@InitRoute
fun initTypesOfSedimentary(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

private val sedimentaryKeyCharacteristics = listOf(
    "Layers" to "Bedding is the giveaway. Sediment settles a layer at a time, so most sedimentary rock shows visible bands, partings, or color changes stacked one on another."
    , "Fossils" to "The only rock type that regularly preserves shells, bone, leaves, and tracks, because nothing here ever got hot enough to destroy them."
    , "Loose, rounded grains" to "Clastic types are built from grains that were carried by water or wind, so the pieces are rounded and sorted rather than grown into each other."
    , "Softer and more porous" to "Many sedimentary rocks scratch easily and soak up water, since the grains are held by cement rather than fused together."
    , "Reacts to acid" to "Limestone and other carbonates fizz when a drop of dilute acid hits them — a quick field test that rules out most other rocks."
)

/** The three ways sediment turns into rock. */
private val sedimentaryFamilies = listOf(
    "Clastic" to "Broken pieces of older rock — gravel, sand, silt, and clay — buried, compacted, and cemented together. Sandstone, shale, and conglomerate are all clastic."
    , "Chemical" to "Minerals precipitated straight out of water as it evaporated or became saturated. Rock salt, gypsum, chert, and much limestone form this way."
    , "Organic (biochemical)" to "Built from the remains of living things: shell and coral fragments in limestone, plant matter compressed into coal, microscopic silica shells in diatomite."
)

/** Named sedimentary rocks, paired with what sets each one apart. */
private val sedimentaryExamples = listOf(
    "Sandstone" to "Cemented sand grains, gritty to the touch. Colors run cream to deep red depending on how much iron the cement carries."
    , "Shale" to "Compacted clay and silt that splits into thin sheets. The most abundant sedimentary rock, and a common host for fossils."
    , "Siltstone" to "Between sandstone and shale in grain size; feels faintly gritty but does not split as cleanly as shale."
    , "Limestone" to "Calcium carbonate from shells, coral, and direct precipitation. Soft enough to scratch with a knife, and it fizzes in acid."
    , "Conglomerate" to "Rounded pebbles set in a finer matrix — an old streambed or beach gravel turned to stone."
    , "Breccia" to "Like conglomerate, but the fragments are angular, meaning they were buried close to where they broke rather than tumbled downstream."
    , "Chert" to "Dense microcrystalline silica, often formed from the shells of marine microorganisms. Flint is a dark variety, and both break with sharp conchoidal edges."
    , "Coal" to "Plant material from ancient swamps, buried and compressed until it became a rock you can burn."
    , "Rock Salt and Gypsum" to "Evaporites left behind when a shallow sea or lake dried up. Soft, and gypsum can be scratched with a fingernail."
    , "Diatomite" to "Chalky, remarkably light rock built from the silica shells of diatoms."
)

@Composable
private fun SedimentaryFigure(src: String, alt: String, caption: String, credit: String) {
    Column(
        Modifier
            .fillMaxWidth()
            .gap(0.5.cssRem)
        , horizontalAlignment = CenterHorizontally
    ) {
        Image(
            src = src
            , alt = alt
            , modifier = Modifier
                .maxWidth(FIGURE_IMAGE_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
        P { Em { Text(caption) } }
        SpanText(credit, ImageCreditStyle.toModifier())
    }
}

@Composable
private fun SedimentarySectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun TypesOfSedimentaryHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Sedimentary Rocks"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfSedimentaryHero() {

    TypesOfSedimentaryHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/sedimentary/grand_canyon_strata.jpg"
                    , alt = "Stacked sedimentary layers in the Grand Canyon"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                P { Em { Text("The Grand Canyon, Arizona — layer on layer of sedimentary rock, read from the bottom up as a record of nearly two billion years.") } }
                SpanText(
                    "Photo: Luca Galuzzi, CC BY-SA 2.5, via Wikimedia Commons"
                    , ImageCreditStyle.toModifier()
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Sedimentary rock is the record keeper of the three types. It forms at the Earth's surface, at ordinary temperatures, from material that was worn off older rock or left behind by living things. Because it never gets cooked, it holds onto the evidence of the world it formed in — the fossils, the ripple marks, the layer-by-layer order of events.")
            }

            SedimentarySectionTitle("How They Form")
            P {
                Text("Weathering breaks older rock into fragments, and water, wind, and ice carry those fragments downhill until the current slows and drops them. Layers pile up in river valleys, lakes, deltas, and on the sea floor. Burial squeezes the water out and packs the grains together, and minerals carried in groundwater cement the whole thing into solid rock. Some sedimentary rock skips the fragments entirely and precipitates directly out of water, or accumulates from shells and plant debris.")
            }
        }
    }

    Column(
        HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Ul(PageListStyle.toAttrs()) {
            sedimentaryFamilies.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        SedimentaryFigure(
            src = "/images/typesof/sedimentary/zion_cross_bedded_sandstone.jpg"
            , alt = "Sweeping cross-beds in sandstone at Zion National Park"
            , caption = "Cross-bedded sandstone on the Angel's Landing Trail, Zion National Park, Utah — the frozen slip faces of ancient sand dunes, each sweeping set recording the wind that built it."
            , credit = "Photo: Roy Luck, CC BY 2.0, via Wikimedia Commons"
        )
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        SedimentarySectionTitle("Key Characteristics")

        Ul(PageListStyle.toAttrs()) {
            sedimentaryKeyCharacteristics.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        SedimentaryFigure(
            src = "/images/typesof/sedimentary/sandstone_bedding_ohio.jpg"
            , alt = "Bedded sandstone outcrop weathering along its layers"
            , caption = "Quartzose sandstone near Dundee, Ohio — bedding planes stand out as the softer layers weather back faster than the hard ones."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        SedimentaryFigure(
            src = "/images/typesof/sedimentary/coquina_florida.jpg"
            , alt = "Coquina made of cemented seashell fragments"
            , caption = "Coquina from Cayo Costa Island, Florida — limestone that is nothing but shells, cemented together with the pieces still recognizable."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        SedimentarySectionTitle("Common Examples")

        Ul(PageListStyle.toAttrs()) {
            sedimentaryExamples.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        SedimentaryFigure(
            src = "/images/typesof/sedimentary/conglomerate_kentucky.jpg"
            , alt = "Conglomerate with rounded quartz pebbles in a red matrix"
            , caption = "Quartz-pebble conglomerate near Olive Hill, Kentucky — rounded pebbles, tumbled smooth in moving water before they were buried and cemented."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        SedimentaryFigure(
            src = "/images/typesof/sedimentary/shale_ohio.jpg"
            , alt = "Shale outcrop breaking down into flat chips"
            , caption = "Shale at the Beach City Dam outcrop, Ohio — compacted mud that splits into thin sheets and crumbles into chips at the base of the exposure."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        SedimentarySectionTitle("For Rockhounds")
        P {
            Text("Sedimentary country is fossil country, and it is also where petrified wood turns up, since buried logs sit in exactly the sediment these rocks are made of. Look at freshly cut banks, quarry faces, and gravel bars where a season of high water has exposed something new. Softer layers weather back faster than hard ones, so the resistant nodules — chert, agate, silicified wood — end up loose on the surface waiting to be picked up.")
        }
    }
}

@Page("/types_of_rocks/sedimentary")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfSedimentaryPage() {
    TypesOfSedimentaryHero()
}
